import React from 'react';
import { Alert, Box, Button, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { IBillingCustomerInvoice } from '../../compiler/billing.interface';
import { buildPaymentAllocationPreview } from './billingPaymentWorkflow';
import { formatThaiCalendarDate } from '../../utilities/bangkokDateTime';

const money = (amount: number) => amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
interface Props {
  invoices: IBillingCustomerInvoice[];
  amount: number;
  onRemove?: (uuid: string) => void;
  showSummary?: boolean;
}
export default function BillingPaymentAllocationPreview({ invoices, amount, onRemove, showSummary = true }: Props) {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const preview = React.useMemo(() => buildPaymentAllocationPreview(invoices, amount), [invoices, amount]);
  const rows = preview.rows.filter(row => row.invoice_number.toLowerCase().includes(search.trim().toLowerCase()));
  const currentPage = Math.min(page, Math.max(0, Math.ceil(rows.length / 10) - 1));
  return <Stack spacing={1.5} sx={{ mt: 2, minWidth: 0 }}>
    <Typography fontWeight={700}>{onRemove ? 'บิลที่เลือกทั้งหมด' : 'ตรวจการตัดยอดก่อนบันทึก'}</Typography>
    {!onRemove && showSummary && <BillingPaymentSummary invoices={invoices} amount={amount} />}
    <TextField size="small" label="ค้นหาในบิลที่เลือก" value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} />
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={onRemove ? 'รายการบิลที่เลือก' : 'ผลการจัดสรรยอดรับชำระ'}>
        <TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>วันที่</TableCell><TableCell align="right">ค้างก่อนรับ</TableCell>
          {!onRemove && <><TableCell align="right">รับครั้งนี้</TableCell><TableCell align="right">คงเหลือ</TableCell></>}
          {onRemove && <TableCell>การเลือก</TableCell>}
        </TableRow></TableHead>
        <TableBody>{rows.slice(currentPage * 10, currentPage * 10 + 10).map(row => <TableRow key={row.uuid}>
          <TableCell>{row.invoice_number}</TableCell><TableCell>{formatThaiCalendarDate(row.invoice_date)}</TableCell>
          <TableCell align="right">{money(Number(row.outstanding_amount))}</TableCell>
          {!onRemove && <><TableCell align="right">{money(row.allocated)}</TableCell><TableCell align="right">{money(row.remaining)}</TableCell></>}
          {onRemove && <TableCell><Button size="small" onClick={() => onRemove(row.uuid)} aria-label={`นำ ${row.invoice_number} ออกจากรายการที่เลือก`}>นำออก</Button></TableCell>}
        </TableRow>)}
        {!rows.length && <TableRow><TableCell colSpan={5}>ไม่พบบิลที่เลือกตามคำค้น</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Box>
    <TablePagination component="div" count={rows.length} rowsPerPage={10} rowsPerPageOptions={[10]} page={currentPage}
      onPageChange={(_, next) => setPage(next)} labelRowsPerPage="แสดง" labelDisplayedRows={({ from, to, count }) => `${from}–${to} จาก ${count} ใบ`}
      getItemAriaLabel={type => type === 'next' ? 'หน้าถัดไป' : 'หน้าก่อนหน้า'} />
  </Stack>;
}

export function BillingPaymentSummary({ invoices, amount }: Pick<Props, 'invoices' | 'amount'>) {
  const preview = React.useMemo(() => buildPaymentAllocationPreview(invoices, amount), [invoices, amount]);
  return <>
      <Alert severity="info">ตัดตามวันที่ Invoice เก่าก่อน บิลสุดท้ายชำระบางส่วนได้ ยอดหนี้จะลดเมื่ออนุมัติเท่านั้น</Alert>
      <Stack direction="row" gap={2} flexWrap="wrap" aria-live="polite">
        <Typography>ยอดค้างที่เลือก {money(preview.total)} บาท</Typography>
        <Typography>รับครั้งนี้ {money(amount || 0)} บาท</Typography>
        <Typography fontWeight={700}>คงเหลือหลังอนุมัติ {money(preview.remaining)} บาท</Typography>
      </Stack>
      <Typography>ปิดครบ {preview.paidCount} ใบ · ชำระบางส่วน {preview.partialCount} ใบ · ยังไม่ตัดยอด {preview.untouchedCount} ใบ</Typography>
  </>;
}
